import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useExecutions } from '../hooks/useExecutions'
import { ExecutionStatus } from '../components/ExecutionStatus'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ErrorAlert } from '../components/ErrorAlert'

export function ExecutionView() {
  const params = useParams()
  const { reportId, executionTime: encodedExecutionTime } = params

  const { loading, error, getExecution, retryExecution, resendEmail } = useExecutions()
  const [execution, setExecution] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState(null)

  // Decode the execution time from the URL
  const executionTime = encodedExecutionTime ? decodeURIComponent(encodedExecutionTime) : undefined

  useEffect(() => {
    loadExecution()
  }, [reportId, executionTime])

  async function loadExecution() {
    try {
      // Create execution ID from report ID and time (API expects # separator)
      const execId = `${reportId}#${executionTime}`
      const data = await getExecution(execId)
      setExecution(data)
    } catch (err) {
      console.error('Failed to load execution:', err)
    }
  }

  async function handleRetry() {
    setActionLoading(true)
    setActionError(null)
    try {
      const execId = `${reportId}#${executionTime}`
      await retryExecution(execId)
      await loadExecution()
    } catch (err) {
      setActionError('Failed to retry execution')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleResend() {
    setActionLoading(true)
    setActionError(null)
    try {
      const execId = `${reportId}#${executionTime}`
      await resendEmail(execId)
      await loadExecution()
    } catch (err) {
      setActionError('Failed to resend email')
    } finally {
      setActionLoading(false)
    }
  }

  function formatDate(isoString) {
    const date = new Date(isoString)
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  function formatDuration(ms) {
    if (ms < 1000) return `${ms}ms`
    const seconds = (ms / 1000).toFixed(1)
    return `${seconds}s`
  }

  if (loading && !execution) {
    return <LoadingSpinner message="Loading execution..." />
  }

  if (!execution) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Execution not found</p>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <Link
          to={`/reports/${reportId}/history`}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium mb-2 inline-block"
        >
          ← Back to History
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{execution.report_name}</h1>
        <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
          Executed on {formatDate(execution.execution_time)}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1 order-2 lg:order-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:sticky lg:top-6">
            <h2 className="text-sm font-semibold text-gray-900 uppercase mb-4">Details</h2>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <ExecutionStatus status={execution.status} />
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Duration</p>
                <p className="text-sm font-medium text-gray-900">
                  {execution.duration_ms ? formatDuration(execution.duration_ms) : 'N/A'}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Email Sent</p>
                <p className="text-sm font-medium">
                  {execution.email_sent ? (
                    <span className="text-green-600">✓ Yes</span>
                  ) : (
                    <span className="text-gray-600">✗ No</span>
                  )}
                </p>
              </div>

              {execution.error_message && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Error</p>
                  <p className="text-sm text-red-600">{execution.error_message}</p>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 space-y-2">
              {execution.status === 'failed' && (
                <button
                  onClick={handleRetry}
                  disabled={actionLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base"
                >
                  {actionLoading ? 'Retrying...' : 'Retry Execution'}
                </button>
              )}

              {execution.status === 'completed' && execution.agent_response && !execution.email_sent && (
                <button
                  onClick={handleResend}
                  disabled={actionLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base"
                >
                  {actionLoading ? 'Sending...' : 'Resend Email'}
                </button>
              )}

              {actionError && (
                <p className="text-sm text-red-600">{actionError}</p>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 order-1 lg:order-2">
          {error && (
            <div className="mb-6">
              <ErrorAlert message={error} />
            </div>
          )}

          {/* Audio Brief Section */}
          {execution.audio_url && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200 p-4 sm:p-6 mb-6">
              <div className="flex items-start sm:items-center gap-3 mb-3">
                <span className="text-2xl">🎧</span>
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900">Audio Brief</h2>
                  <p className="text-xs sm:text-sm text-gray-600">Listen to a shorter version of this report</p>
                </div>
              </div>
              <audio
                controls
                className="w-full"
                style={{ maxHeight: '40px' }}
              >
                <source src={execution.audio_url} type="audio/wav" />
                Your browser does not support audio playback.
              </audio>
            </div>
          )}

          {execution.agent_response ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
              <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({node, ...props}) => <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4 pb-2 border-b-2 border-gray-200" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mt-5 sm:mt-6 mb-2 sm:mb-3" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mt-4 sm:mt-5 mb-2" {...props} />,
                    p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 mb-4 text-gray-700" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-2 mb-4 text-gray-700" {...props} />,
                    li: ({node, ...props}) => <li className="ml-4" {...props} />,
                    a: ({node, ...props}) => <a className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer" {...props} />,
                    code: ({node, inline, ...props}) =>
                      inline ?
                        <code className="bg-gray-100 text-red-600 px-1.5 py-0.5 rounded text-sm font-mono" {...props} /> :
                        <code className="block bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm font-mono border-l-4 border-blue-500" {...props} />,
                    blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-600 my-4" {...props} />,
                    table: ({node, ...props}) => (
                      <div className="overflow-x-auto my-4">
                        <table className="min-w-full divide-y divide-gray-200" {...props} />
                      </div>
                    ),
                    thead: ({node, ...props}) => <thead className="bg-gray-50" {...props} />,
                    tbody: ({node, ...props}) => <tbody className="bg-white divide-y divide-gray-200" {...props} />,
                    tr: ({node, ...props}) => <tr {...props} />,
                    th: ({node, ...props}) => <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" {...props} />,
                    td: ({node, ...props}) => <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-700" {...props} />,
                    hr: ({node, ...props}) => <hr className="my-8 border-t-2 border-gray-200" {...props} />
                  }}
                >
                  {execution.agent_response}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
              <p className="text-gray-600">No report content available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
